import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import ExpenseTypeFormModal from '../components/ExpenseTypeFormModal';

// TODO: role-based access (limit to owner/admin in future)

const SettingsTab = () => {
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const fetchExpenseTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await chemicalService.getExpenseTypes({ page: 1, limit: 500 });
      setExpenseTypes(res.data || []);
    } catch (err) {
      console.error('Error fetching expense types:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load expense types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenseTypes();
  }, [fetchExpenseTypes]);

  const handleAddClick = () => {
    setEditingType(null);
    setShowFormModal(true);
  };

  const handleEditClick = (type) => {
    setEditingType(type);
    setShowFormModal(true);
  };

  const handleDelete = async (type) => {
    if (!window.confirm(
      `Delete expense type "${type.name}"?\n\n` +
      `If any expenses use this type, the deletion will be blocked.`
    )) return;

    try {
      await chemicalService.deleteExpenseType(type.id);
      alert('✅ Expense type deleted.');
      fetchExpenseTypes();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingType(null);
    fetchExpenseTypes();
  };

  const activeCount = expenseTypes.filter((t) => t.is_active).length;
  const retiredCount = expenseTypes.length - activeCount;

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Page heading ─── */}
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle}>⚙️ Settings</h2>
        <p style={styles.pageSubtitle}>
          Configure module-wide settings and categories
        </p>
      </div>

      {/* ─── Section: Expense Types ─── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>Expense Types</h3>
            <p style={styles.sectionSubtitle}>
              Categories used to organize expenses (e.g. Rent, Utilities, Fuel)
            </p>
          </div>
          <button onClick={handleAddClick} style={styles.addButton}>
            + New Type
          </button>
        </div>

        {/* Mini stats */}
        <div style={styles.miniStats}>
          <div style={styles.miniStat}>
            <span style={styles.miniStatLabel}>Total</span>
            <span style={styles.miniStatValue}>{expenseTypes.length}</span>
          </div>
          <div style={styles.miniStat}>
            <span style={styles.miniStatLabel}>Active</span>
            <span style={{ ...styles.miniStatValue, color: '#27ae60' }}>{activeCount}</span>
          </div>
          <div style={styles.miniStat}>
            <span style={styles.miniStatLabel}>Retired</span>
            <span style={{ ...styles.miniStatValue, color: '#95a5a6' }}>{retiredCount}</span>
          </div>
        </div>

        {/* Types grid */}
        {loading ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
            Loading expense types...
          </div>
        ) : expenseTypes.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
              No expense types yet
            </div>
            <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
              Click "+ New Type" to create your first category.<br />
              Try: Rent, Utilities, Fuel, Salaries, Maintenance, Office Supplies, Miscellaneous
            </div>
          </div>
        ) : (
          <div style={styles.typesGrid}>
            {expenseTypes.map((type) => (
              <div
                key={type.id}
                style={{
                  ...styles.typeCard,
                  opacity: type.is_active ? 1 : 0.55,
                  borderLeft: `4px solid ${type.color || '#7f8c8d'}`,
                }}
              >
                <div style={styles.typeCardLeft}>
                  <div style={{
                    ...styles.colorDot,
                    background: type.color || '#7f8c8d',
                  }} />
                  <div>
                    <div style={styles.typeName}>{type.name}</div>
                    <div style={styles.typeMeta}>
                      {type.is_active ? (
                        <span style={styles.activeBadge}>Active</span>
                      ) : (
                        <span style={styles.retiredBadge}>Retired</span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={styles.typeCardActions}>
                  <button
                    onClick={() => handleEditClick(type)}
                    style={styles.iconButton}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(type)}
                    style={{ ...styles.iconButton, background: '#e74c3c' }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Placeholder for future sections ─── */}
      <div style={{ ...styles.section, opacity: 0.6 }}>
        <div style={styles.sectionHeader}>
          <div>
            <h3 style={styles.sectionTitle}>More settings coming soon</h3>
            <p style={styles.sectionSubtitle}>
              Future: tax rates, payment modes, units of measure, locations, and more
            </p>
          </div>
        </div>
      </div>

      {/* ─── Modal ─── */}
      {showFormModal && (
        <ExpenseTypeFormModal
          expenseType={editingType}
          onClose={() => { setShowFormModal(false); setEditingType(null); }}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

const styles = {
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px', fontWeight: '700',
    color: '#2c3e50', margin: 0,
  },
  pageSubtitle: {
    fontSize: '13px', color: '#7f8c8d',
    marginTop: '4px', margin: '4px 0 0 0',
  },

  section: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px 24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginBottom: '20px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0',
  },
  sectionTitle: {
    fontSize: '18px', fontWeight: '700',
    color: '#2c3e50', margin: 0,
  },
  sectionSubtitle: {
    fontSize: '13px', color: '#7f8c8d',
    margin: '4px 0 0 0',
  },
  addButton: {
    padding: '8px 16px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },

  miniStats: {
    display: 'flex', gap: '16px',
    padding: '10px 14px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  miniStat: {
    display: 'flex', flexDirection: 'column',
    gap: '2px',
  },
  miniStatLabel: {
    fontSize: '11px', color: '#7f8c8d',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  miniStatValue: {
    fontSize: '18px', fontWeight: '700',
    color: '#2c3e50',
  },

  typesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '10px',
  },
  typeCard: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '12px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
  },
  typeCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  colorDot: {
    width: '14px', height: '14px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  typeName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#2c3e50',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  typeMeta: {
    marginTop: '4px',
  },
  activeBadge: {
    background: '#d4edda', color: '#155724',
    padding: '2px 10px', borderRadius: '10px',
    fontSize: '11px', fontWeight: '700',
  },
  retiredBadge: {
    background: '#e2e3e5', color: '#383d41',
    padding: '2px 10px', borderRadius: '10px',
    fontSize: '11px', fontWeight: '700',
  },
  typeCardActions: {
    display: 'flex', gap: '6px', flexShrink: 0,
  },
  iconButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px',
  },

  emptyState: {
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#95a5a6',
    fontSize: '14px',
  },
};

export default SettingsTab;